import CircleBg from "@assets/icons/circles-bg-login.svg";
import styled from "@emotion/styled";
import { ResponsiveDeviceType, useDimensions } from "@ui/hooks/use-dimensions";
import ProjectDescription from "@ui/pages/login/components/project-description";
import { physioMeMobileBreakpoint, physioMeTabletBreakpoint } from "@ui/style";
import { colors } from "@ui/style/colors";
import { isNil } from "@utils/assert";
import { svgComponentToCSS } from "@utils/svg-utils";
import React, { useMemo } from "react";
import { useResizeDetector } from "react-resize-detector";

interface LoginContainerProps {
	form: JSX.Element;
	showPhysioMeMonochromeLogo?: boolean;
}

const RESPONSIVE_PADDING: Record<ResponsiveDeviceType, number> = {
	[ResponsiveDeviceType.Desktop]: 20,
	[ResponsiveDeviceType.Tablet]: 32,
	[ResponsiveDeviceType.Mobile]: 16,
};

export const LoginContainer: React.FC<LoginContainerProps> = ({ form, showPhysioMeMonochromeLogo }) => {
	const { height, ref } = useResizeDetector();
	const { responsiveDeviceType } = useDimensions();

	const backgroundOffset = useMemo(() => {
		if (isNil(height)) {
			return 0;
		}
		return height + RESPONSIVE_PADDING[responsiveDeviceType] * 2;
	}, [height, responsiveDeviceType]);

	return (
		<Container>
			<BackgroundCircles insetTop={backgroundOffset} />
			<FormContainer ref={ref}>
				<FormCard>{form}</FormCard>
			</FormContainer>
			<ProjectDescription showPhysioMeMonochromeLogo={showPhysioMeMonochromeLogo} />
		</Container>
	);
};

const Container = styled.div`
	background-color: ${colors.mobilePrimaryBlue};
	min-height: 100vh;
	max-width: 100vw;
	flex: 1;
	display: flex;
	flex-direction: row;
	padding: ${RESPONSIVE_PADDING[ResponsiveDeviceType.Desktop]}px;
	gap: 56px;
	overflow: hidden;
	position: relative;

	@media ${physioMeTabletBreakpoint} {
		flex-direction: column;
		padding: 0 0 100px 0;
		gap: 0;
	}
`;

const FormContainer = styled.div`
	z-index: 1;
	display: flex;
	flex: 1;
	justify-content: center;
	@media ${physioMeTabletBreakpoint} {
		padding: ${RESPONSIVE_PADDING[ResponsiveDeviceType.Tablet]}px 118px;
		background-color: ${colors.white};
	}
	@media ${physioMeMobileBreakpoint} {
		padding: ${RESPONSIVE_PADDING[ResponsiveDeviceType.Mobile]}px;
	}
`;

const FormCard = styled.div`
	flex: 1;
	display: flex;
	border-radius: 40px;
	background-color: ${colors.white};
	padding: 80px 118px;
	@media ${physioMeTabletBreakpoint} {
		padding: 0 32px;
		min-width: 500px;
		max-width: 712px;
	}
	@media ${physioMeMobileBreakpoint} {
		padding: 0 16px;
		min-width: unset;
	}
`;

const BackgroundCircles = styled.div<{ insetTop: number }>`
	background-image: ${svgComponentToCSS(<CircleBg width="100%" height="100%" />)};
	background-position-x: 600px;
	background-position-y: 328px;
	-webkit-background-size: cover;
	background-size: cover;
	background-repeat: no-repeat;
	min-height: 100%;
	position: absolute;
	top: 0;
	right: 0;
	left: 0;
	@media ${physioMeTabletBreakpoint} {
		background-position: center bottom;
		right: 0px;
		bottom: -400px;
	}

	@media ${physioMeMobileBreakpoint} {
		background-image: ${svgComponentToCSS(<CircleBg width={375} height="100%" />)};
		top: ${({ insetTop }) => insetTop - 70}px;
	}
`;
